/**
 * 知识抽查系统 - 统计看板
 */
import React, { useState, useEffect } from 'react';
import { Card, Statistic, Table, Tag, message, Spin, Empty, Row, Col, Progress, Button } from 'antd';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftOutlined, CheckCircleOutlined, CloseCircleOutlined, MinusCircleOutlined, BarChartOutlined } from '@ant-design/icons';
import knowledgeLotteryApi from '../services/knowledgeLotteryApi';

const KnowledgeLotteryStatistics = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [overview, setOverview] = useState(null);
  const [memberStats, setMemberStats] = useState([]);
  const [sessionStats, setSessionStats] = useState([]);
  const [questionStats, setQuestionStats] = useState([]);

  useEffect(() => {
    fetchStatistics();
  }, []);

  const fetchStatistics = async () => {
    setLoading(true);
    try {
      // 并行获取所有统计数据
      const [overviewRes, membersRes, sessionsRes, questionsRes] = await Promise.all([
        knowledgeLotteryApi.statisticsApi.getOverview(),
        knowledgeLotteryApi.statisticsApi.getMemberStatistics(),
        knowledgeLotteryApi.statisticsApi.getSessionStatistics(),
        knowledgeLotteryApi.statisticsApi.getQuestionStatistics()
      ]);

      if (overviewRes.success) {
        setOverview(overviewRes.data);
      }
      if (membersRes.success) {
        setMemberStats(membersRes.data || []);
      }
      if (sessionsRes.success) {
        setSessionStats(sessionsRes.data || []);
      }
      if (questionsRes.success) {
        setQuestionStats(questionsRes.data || []);
      }
    } catch (error) {
      console.error('获取统计数据失败:', error);
      message.error(error.message || '获取统计数据失败');
    }
    setLoading(false);
  };

  const formatDate = (date) => {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}年${month}月${day}日`;
  };

  // 成员统计表格列
  const memberColumns = [
    {
      title: '成员姓名',
      dataIndex: 'memberName',
      key: 'memberName',
      fixed: 'left'
    },
    {
      title: '部门',
      dataIndex: 'department',
      key: 'department'
    },
    {
      title: '总答题数',
      dataIndex: 'totalAnswers',
      key: 'totalAnswers',
      sorter: (a, b) => a.totalAnswers - b.totalAnswers
    },
    {
      title: '正确',
      dataIndex: 'correctCount',
      key: 'correctCount',
      render: (count) => <Tag color="success">{count}</Tag>,
      sorter: (a, b) => a.correctCount - b.correctCount
    },
    {
      title: '部分正确',
      dataIndex: 'partialCount',
      key: 'partialCount',
      render: (count) => <Tag color="warning">{count}</Tag>,
      sorter: (a, b) => a.partialCount - b.partialCount
    },
    {
      title: '错误',
      dataIndex: 'incorrectCount',
      key: 'incorrectCount',
      render: (count) => <Tag color="error">{count}</Tag>,
      sorter: (a, b) => a.incorrectCount - b.incorrectCount
    },
    {
      title: '未答题',
      dataIndex: 'unansweredCount',
      key: 'unansweredCount',
      render: (count) => <Tag>{count}</Tag>,
      sorter: (a, b) => a.unansweredCount - b.unansweredCount
    },
    {
      title: '正确率',
      dataIndex: 'correctRate',
      key: 'correctRate',
      render: (rate) => (
        <div>
          <Progress 
            percent={rate} 
            size="small" 
            status={rate >= 80 ? 'success' : rate >= 60 ? 'normal' : 'exception'}
            format={(percent) => `${percent}%`}
          />
        </div>
      ),
      sorter: (a, b) => a.correctRate - b.correctRate
    }
  ];

  // 会议统计表格列
  const sessionColumns = [
    {
      title: '会议标题',
      dataIndex: 'sessionTitle',
      key: 'sessionTitle',
      ellipsis: true
    },
    {
      title: '会议日期',
      dataIndex: 'sessionDate',
      key: 'sessionDate',
      render: (date) => formatDate(date),
      sorter: (a, b) => new Date(a.sessionDate) - new Date(b.sessionDate)
    },
    {
      title: '会议期数',
      key: 'sessionInfo',
      render: (_, record) => {
        if (record.sessionYear && record.sessionNumber) {
          return `${record.sessionYear}年第${record.sessionNumber}期会议`;
        }
        return '-';
      }
    },
    {
      title: '总答题数',
      dataIndex: 'totalAnswers',
      key: 'totalAnswers',
      sorter: (a, b) => a.totalAnswers - b.totalAnswers
    },
    {
      title: '正确',
      dataIndex: 'correctCount',
      key: 'correctCount',
      render: (count) => <Tag color="success">{count}</Tag>
    },
    {
      title: '部分正确',
      dataIndex: 'partialCount',
      key: 'partialCount',
      render: (count) => <Tag color="warning">{count}</Tag>
    },
    {
      title: '错误',
      dataIndex: 'incorrectCount',
      key: 'incorrectCount',
      render: (count) => <Tag color="error">{count}</Tag>
    },
    {
      title: '正确率',
      dataIndex: 'correctRate',
      key: 'correctRate',
      render: (rate) => (
        <Progress 
          percent={rate} 
          size="small" 
          status={rate >= 80 ? 'success' : rate >= 60 ? 'normal' : 'exception'}
          format={(percent) => `${percent}%`}
        />
      ),
      sorter: (a, b) => a.correctRate - b.correctRate
    }
  ];

  // 题目统计表格列
  const questionColumns = [
    {
      title: '题目',
      dataIndex: 'question',
      key: 'question',
      ellipsis: true,
      width: 300
    },
    {
      title: '难度',
      dataIndex: 'difficulty',
      key: 'difficulty',
      render: (difficulty) => {
        const colors = { easy: 'green', medium: 'orange', hard: 'red' };
        const labels = { easy: '简单', medium: '中等', hard: '困难' };
        return <Tag color={colors[difficulty]}>{labels[difficulty]}</Tag>;
      }
    },
    {
      title: '总答题数',
      dataIndex: 'totalAnswers',
      key: 'totalAnswers',
      sorter: (a, b) => a.totalAnswers - b.totalAnswers
    },
    {
      title: '正确',
      dataIndex: 'correctCount',
      key: 'correctCount',
      render: (count) => <Tag color="success">{count}</Tag>
    },
    {
      title: '部分正确',
      dataIndex: 'partialCount',
      key: 'partialCount',
      render: (count) => <Tag color="warning">{count}</Tag>
    },
    {
      title: '错误',
      dataIndex: 'incorrectCount',
      key: 'incorrectCount',
      render: (count) => <Tag color="error">{count}</Tag>
    },
    {
      title: '正确率',
      dataIndex: 'correctRate',
      key: 'correctRate',
      render: (rate) => (
        <Progress 
          percent={rate} 
          size="small" 
          status={rate >= 80 ? 'success' : rate >= 60 ? 'normal' : 'exception'}
          format={(percent) => `${percent}%`}
        />
      ),
      sorter: (a, b) => a.correctRate - b.correctRate
    }
  ];

  if (loading) {
    return (
      <div style={{ padding: '48px', textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      {/* Header */}
      <header style={{ 
        borderBottom: '4px solid #ff4d4f', 
        background: '#fff',
        padding: '16px 24px'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <Button 
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate('/knowledge-lottery')}
            style={{ border: 'none' }}
          >
            返回
          </Button>
          <h1 style={{ fontSize: '20px', fontWeight: 'bold', margin: 0 }}>
            <BarChartOutlined style={{ marginRight: '8px' }} />
            答题统计看板
          </h1>
        </div>
      </header>

      <main style={{ maxWidth: '1400px', margin: '0 auto', padding: '24px' }}>
        {/* 总体统计 */}
        {overview && (
          <Card 
            title="总体统计"
            style={{ 
              border: '2px solid #ff4d4f',
              borderRadius: '8px',
              marginBottom: '24px'
            }}
          >
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="总答题数"
                  value={overview.totalAnswers}
                  prefix={<BarChartOutlined />}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="正确数"
                  value={overview.correctCount}
                  prefix={<CheckCircleOutlined style={{ color: '#52c41a' }} />}
                  valueStyle={{ color: '#52c41a' }}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="部分正确"
                  value={overview.partialCount}
                  prefix={<MinusCircleOutlined style={{ color: '#faad14' }} />}
                  valueStyle={{ color: '#faad14' }}
                />
              </Col>
              <Col xs={24} sm={12} md={6}>
                <Statistic
                  title="错误数"
                  value={overview.incorrectCount}
                  prefix={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                  valueStyle={{ color: '#ff4d4f' }}
                />
              </Col>
            </Row>
            <Row gutter={[16, 16]} style={{ marginTop: '24px' }}>
              <Col xs={24} sm={12}>
                <Statistic
                  title="总分配数"
                  value={overview.totalAssignments}
                />
              </Col>
              <Col xs={24} sm={12}>
                <Statistic
                  title="整体正确率"
                  value={overview.correctRate}
                  suffix="%"
                  valueStyle={{ 
                    color: overview.correctRate >= 80 ? '#52c41a' : 
                           overview.correctRate >= 60 ? '#faad14' : '#ff4d4f'
                  }}
                />
                <Progress 
                  percent={overview.correctRate} 
                  status={overview.correctRate >= 80 ? 'success' : overview.correctRate >= 60 ? 'normal' : 'exception'}
                  style={{ marginTop: '8px' }}
                />
              </Col>
            </Row>
          </Card>
        )}

        {/* 成员统计 */}
        <Card 
          title="成员答题统计"
          style={{ 
            border: '2px solid #ff4d4f',
            borderRadius: '8px',
            marginBottom: '24px'
          }}
        >
          {memberStats.length === 0 ? (
            <Empty description="暂无统计数据" />
          ) : (
            <Table
              columns={memberColumns}
              dataSource={memberStats}
              rowKey="memberId"
              pagination={{
                showTotal: (total, range) => `共 ${total} 条`,
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true
              }}
              scroll={{ x: 1000 }}
            />
          )}
        </Card>

        {/* 会议统计 */}
        <Card 
          title="会议答题统计"
          style={{ 
            border: '2px solid #ff4d4f',
            borderRadius: '8px',
            marginBottom: '24px'
          }}
        >
          {sessionStats.length === 0 ? (
            <Empty description="暂无统计数据" />
          ) : (
            <Table
              columns={sessionColumns}
              dataSource={sessionStats}
              rowKey="sessionId"
              pagination={{
                showTotal: (total, range) => `共 ${total} 条`,
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true
              }}
              scroll={{ x: 1200 }}
            />
          )}
        </Card>

        {/* 题目统计 */}
        <Card 
          title="题目答题统计"
          style={{ 
            border: '2px solid #ff4d4f',
            borderRadius: '8px'
          }}
        >
          {questionStats.length === 0 ? (
            <Empty description="暂无统计数据" />
          ) : (
            <Table
              columns={questionColumns}
              dataSource={questionStats}
              rowKey="questionId"
              pagination={{
                showTotal: (total, range) => `共 ${total} 条`,
                pageSize: 10,
                showSizeChanger: true,
                showQuickJumper: true
              }}
              scroll={{ x: 1200 }}
            />
          )}
        </Card>
      </main>
    </div>
  );
};

export default KnowledgeLotteryStatistics;
